import UserTokenProfile from "@/domain/entities/dashboard/UserTokenProfile";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import { TokenStatus, useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isValid, parseISO } from "date-fns";
import { useEffect, useMemo } from "react";
import useGetEventBus from "./useGetEventBus";
import useGetUserProfile from "./useGetUserProfile";
import useFetchErrorNotifier from "./utils/useFetchErrorNotifier";

const renewalDateFromNextQuotaIso = (
    iso: string | null | undefined,
): Date | undefined => {
    if (!iso) return undefined;
    const parsed = parseISO(iso);
    return isValid(parsed) ? parsed : undefined;
};

const useGetTokenProfile = () => {
    const notifyFetchError = useFetchErrorNotifier();
    const { iaManager } = useSdk();
    const queryClient = useQueryClient();
    const { data: user } = useGetUserProfile();

    const fetchTokenProfile = async () => {
        try {
            const profile = await iaManager.getUserTokenProfile();
            if (!profile) return null;

            return {
                used: profile.monthlyTokenCount - profile.currentTokenCount,
                total: profile.monthlyTokenCount,
            };
        } catch (error) {
            console.error(error);
            notifyFetchError({
                queryKey: queryKeys.tokenProfile(),
                titleTranslationKey: "errors.tokenProfile",
            });

            throw error;
        }
    };

    const tokenCountsQuery = useQuery({
        queryKey: queryKeys.tokenProfile(),
        queryFn: fetchTokenProfile,
        // Data is only updated via eventKeys.token.iaUpdated event (setQueryData below),
        // never from a background refetch.
        staleTime: STALE_TIME.session,
        throwOnError: false,
    });

    const handleTokenUpdated = (tokenStatus: TokenStatus) => {
        queryClient.setQueryData(queryKeys.tokenProfile(), {
            used: tokenStatus.used,
            total: tokenStatus.total,
        });
    };

    const { off } = useGetEventBus<TokenStatus>(
        eventKeys.token.iaUpdated,
        handleTokenUpdated,
    );

    useEffect(
        () => () => {
            off(eventKeys.token.iaUpdated, handleTokenUpdated);
        },
        [],
    );

    // Combine token counts (from query) with renewalDate (from user profile).
    // Both are independent async sources, so the merge happens here.
    const data = useMemo(() => {
        if (!tokenCountsQuery.data) return null;

        return new UserTokenProfile({
            ...tokenCountsQuery.data,
            renewalDate: renewalDateFromNextQuotaIso(
                user?.nextQuotaRenewalDate,
            ),
        });
    }, [tokenCountsQuery.data, user?.nextQuotaRenewalDate]);

    return { data };
};

export default useGetTokenProfile;
